import { useSelector } from 'react-redux';
import type { SupplierAssessment, UserOrganisation } from '../../types/types';
import { useSupplierAssessments } from '../../hooks/useSupplierAssessments';
import {
  Card,
  Row,
  Col,
  Spinner,
  Button,
  Container,
  Badge,
  Alert,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getTotalScorePercentage, getBarColor } from '../../util/util';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import { useRef } from 'react';
import ErrorComponent from '../common/Error';

const dashboardStyles = {
  shell: {
    background:
      'linear-gradient(180deg, #f7fafc 0%, #f1f5f9 40%, #f8fafc 100%)',
    borderRadius: '28px',
    padding: '1.25rem',
  },
  hero: {
    border: '1px solid #dbe4ee',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
    background: '#ffffff',
  },
  heroHeader: {
    background:
      'linear-gradient(135deg, #ffffff 0%, #f8fbfd 60%, #eef4f8 100%)',
    borderBottom: '1px solid #dbe4ee',
  },
  statCard: {
    border: '1px solid #dbe4ee',
    borderRadius: '20px',
    background: '#ffffff',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
  },
  panel: {
    border: '1px solid #dbe4ee',
    borderRadius: '22px',
    background: '#ffffff',
    boxShadow: '0 14px 32px rgba(15, 23, 42, 0.05)',
    overflow: 'hidden',
  },
  panelHeader: {
    background: '#f8fbfd',
    borderBottom: '1px solid #e2e8f0',
  },
  taskCard: {
    border: '1px solid #dbe4ee',
    borderRadius: '18px',
    background: '#fbfdff',
  },
} satisfies Record<string, React.CSSProperties>;

export default function SupplierDashboard() {
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const navigate = useNavigate();

  const nextActionsRef = useRef<HTMLHeadingElement>(null);

  const { assessments, isLoading, error } = useSupplierAssessments(
    selectedOrganisation?.organisation?.id,
  );

  if (isLoading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );

  if (error) {
    return <ErrorComponent message={error} />;
  }

  // ─── Derivations ──────────────────────────────────────────────
  const assigned = assessments.filter((a) => !a.is_self_assessment);

  const actionRequired = assessments.filter((a) => a.status !== 'completed');

  const completed = assessments.filter((a) => a.status === 'completed');

  // const selfAssessments = assessments.filter((a) => a.is_self_assessment);
  // const published = selfAssessments.filter((a) => a.is_report_published);

  const buyers = assigned
    .map((a) => a.assigning_organisation_name)
    .filter((value, index, self) => self.indexOf(value) === index); // Unique

  // Sort next actions by created date
  const nextActions = [...actionRequired].sort((a, b) => {
    const da = a.created_at ? new Date(a.created_at).getTime() : 0;
    const db = b.created_at ? new Date(b.created_at).getTime() : 0;
    return da - db;
  });

  /**
   * {
  "assessment_id": "1ddfc41d-1838-4dd6-b93a-184b411efc83",
  "assigning_organisation_name": "Supplier Thirty",
  "created_at": "2026-02-16T21:00:11.745+00:00",
  "is_report_published": false,
  "is_self_assessment": true,
  "max_score": null,
  "metadata": null,
  "published_report": null,
  "started_at": "2026-02-16T21:00:11.771762+00:00",
  "status": "created",
  "submitted_at": null,
  "template_id": "313bc74b-1b6e-4320-b98c-70934840c681",
  "template_status": "published",
  "template_title": "Supplier ESG Report",
  "template_version": 1,
  "template_version_number": 1,
  "total_score": 0,
  "updated_at": "2026-02-16T21:00:11.745+00:00"
}
   */

  const statusBadge = (assessment: SupplierAssessment) => {
    if (assessment.status === 'created') {
      return <Badge bg="secondary">Not started</Badge>;
    } else if (assessment.status === 'started') {
      return <Badge bg="warning">Started</Badge>;
    } else if (assessment.status === 'completed') {
      return <Badge bg="success">Done</Badge>;
    }

    return <Badge bg="success">{assessment.status}</Badge>;
  };

  // ─── UI ───────────────────────────────────────────────────────
  return (
    <Container fluid>
      <Card style={dashboardStyles.hero} className="mb-4">
        <div style={dashboardStyles.heroHeader} className="p-4 p-lg-5">
          <Row className="g-4 align-items-center">
            <Col lg={8}>
              <div
                className="text-uppercase fw-semibold mb-2"
                style={{
                  letterSpacing: '0.12em',
                  fontSize: '0.78rem',
                  color: '#64748b',
                }}
              >
                Supplier Workspace
              </div>
              <h2 className="mb-2">
                Welcome, {selectedOrganisation?.organisation?.name}
              </h2>
              <p className="text-muted mb-0">
                Keep track of open assessments, recent completions, and the next
                tasks that need your attention.
              </p>
            </Col>
            <Col lg={4}>
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={() => navigate('/self-assessments')}
                >
                  Open Self-Assessments
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/my-profile')}
                >
                  Manage Company Profile
                </Button>
              </div>
            </Col>
          </Row>
        </div>
        <Card.Body className="p-4">
          <Row className="g-3">
            <Col sm={6} xl={3}>
              <Card style={dashboardStyles.statCard} className="h-100">
                <Card.Body>
                  <div className="text-danger fw-semibold small text-uppercase mb-2">
                    Action Required
                  </div>
                  <div className="d-flex align-items-end justify-content-between gap-3">
                    <span className="display-6 mb-0">
                      {actionRequired.length}
                    </span>
                    <i className="bi bi-exclamation-circle fs-3 text-danger"></i>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card style={dashboardStyles.statCard} className="h-100">
                <Card.Body>
                  <div className="text-warning fw-semibold small text-uppercase mb-2">
                    Assigned
                  </div>
                  <div className="d-flex align-items-end justify-content-between gap-3">
                    <span className="display-6 mb-0">{assigned.length}</span>
                    <i className="bi bi-file-earmark-check fs-3 text-warning"></i>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card style={dashboardStyles.statCard} className="h-100">
                <Card.Body>
                  <div className="text-success fw-semibold small text-uppercase mb-2">
                    Completed
                  </div>
                  <div className="d-flex align-items-end justify-content-between gap-3">
                    <span className="display-6 mb-0">{completed.length}</span>
                    <i className="bi bi-check-circle fs-3 text-success"></i>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card style={dashboardStyles.statCard} className="h-100">
                <Card.Body>
                  <div className="text-info fw-semibold small text-uppercase mb-2">
                    Buyers
                  </div>
                  <div className="d-flex align-items-end justify-content-between gap-3">
                    <span className="display-6 mb-0">{buyers.length}</span>
                    <i className="bi bi-buildings fs-3 text-info"></i>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Alert
        variant="info"
        className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4"
      >
        <div className="d-flex align-items-start gap-2">
          <i className="bi bi-info-circle-fill mt-1"></i>
          <div>
            Build your compliance profile to showcase your efforts to buyers and
            improve visibility across opportunities.
          </div>
        </div>
        <Alert.Link href="/my-profile" className="text-nowrap">
          Create your profile →
        </Alert.Link>
      </Alert>

      {assessments.length === 0 && (
        <Card
          className="text-center"
          style={{
            ...dashboardStyles.panel,
            borderStyle: 'dashed',
            borderWidth: '2px',
          }}
        >
          <Card.Body className="py-5">
            <h5>No assessments yet</h5>
            <p className="text-muted mb-4">
              Start a self-assessment or ask buyers to assign one to you.
            </p>
            <Button onClick={() => navigate('/self-assessments')}>
              Start self-assessment
            </Button>
          </Card.Body>
        </Card>
      )}

      {assessments.length > 0 && (
        <Row className="g-4">
          <Col xl={8}>
            {nextActions.length > 0 && (
              <Card style={dashboardStyles.panel} className="mb-4">
                <div
                  style={dashboardStyles.panelHeader}
                  className="p-3 p-md-4 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2"
                >
                  <div>
                    <h5 className="mb-1" ref={nextActionsRef}>
                      Your next actions
                    </h5>
                    <div className="text-muted small">
                      Prioritized tasks that are still waiting on you.
                    </div>
                  </div>
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={() => navigate('/assessments')}
                  >
                    View all assessments →
                  </Button>
                </div>
                <Card.Body className="p-3 p-md-4">
                  <Row xs={1} md={2} className="g-3">
                    {nextActions.slice(0, 4).map((a, index) => (
                      <Col key={a.assessment_id ?? `next-action-${index}`}>
                        <Card
                          style={dashboardStyles.taskCard}
                          className="h-100"
                        >
                          <Card.Body className="d-flex flex-column">
                            <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                              <div>
                                <Card.Title className="mb-1">
                                  {a.template_title}
                                </Card.Title>
                                <Card.Subtitle className="text-muted small">
                                  {a.is_self_assessment
                                    ? 'Self-assessment'
                                    : `Assigned by ${a.assigning_organisation_name}`}
                                </Card.Subtitle>
                              </div>
                              {a.status && statusBadge(a)}
                            </div>
                            <div className="text-muted small mb-4">
                              Created{' '}
                              {a.created_at
                                ? new Date(a.created_at).toLocaleDateString()
                                : 'recently'}
                            </div>
                            <div className="mt-auto d-grid d-sm-flex justify-content-sm-end">
                              <Button
                                size="sm"
                                onClick={() =>
                                  navigate(`/assessment/${a.assessment_id}`)
                                }
                              >
                                {a.status === 'created' ? 'Start' : 'Continue'}
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            )}

            {completed.length > 0 && (
              <Card style={dashboardStyles.panel}>
                <div style={dashboardStyles.panelHeader} className="p-3 p-md-4">
                  <h5 className="mb-1">Recently completed</h5>
                  <div className="text-muted small">
                    Review recent results and open published reports.
                  </div>
                </div>
                <Card.Body className="p-3 p-md-4">
                  <Row xs={1} md={2} className="g-3">
                    {completed.slice(0, 4).map((a, index) => (
                      <Col key={a.assessment_id ?? `completed-${index}`}>
                        <Card
                          style={dashboardStyles.taskCard}
                          className="h-100"
                        >
                          <Card.Body>
                            <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                              <div>
                                <h6 className="mb-1">{a.template_title}</h6>
                                <div className="text-muted small">
                                  {a.is_self_assessment
                                    ? 'Self-assessment'
                                    : `Assigned by ${a.assigning_organisation_name}`}
                                </div>
                              </div>
                              {statusBadge(a)}
                            </div>
                            <div className="d-flex flex-column flex-sm-row align-items-center gap-4">
                              <div style={{ width: '120px', height: '120px' }}>
                                <CircularProgressbar
                                  value={getTotalScorePercentage(
                                    a.total_score,
                                    a.max_score,
                                  )}
                                  text={`${getTotalScorePercentage(
                                    a.total_score,
                                    a.max_score,
                                  )}%`}
                                  styles={buildStyles({
                                    pathColor: getBarColor(
                                      getTotalScorePercentage(
                                        a.total_score,
                                        a.max_score,
                                      ),
                                    ),
                                    trailColor: '#E5E7EB',
                                    textColor: '#111827',
                                    textSize: '16px',
                                  })}
                                />
                              </div>
                              <div className="flex-grow-1 w-100">
                                <div className="text-muted small mb-1">
                                  Total Score
                                </div>
                                <div className="fs-4 fw-semibold mb-3">
                                  {getTotalScorePercentage(
                                    a.total_score,
                                    a.max_score,
                                  )}
                                  %
                                </div>
                                <div className="d-grid d-sm-flex justify-content-sm-end">
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() =>
                                      navigate(
                                        `/assessmentResult/${a.assessment_id}`,
                                      )
                                    }
                                  >
                                    View report
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            )}
          </Col>

          <Col xl={4}>
            <Card style={dashboardStyles.panel} className="mb-4">
              <div style={dashboardStyles.panelHeader} className="p-3 p-md-4">
                <h5 className="mb-1">At a glance</h5>
                <div className="text-muted small">
                  Quick status for your current workload.
                </div>
              </div>
              <Card.Body className="p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-muted">Open tasks</span>
                  <span className="fw-semibold">{nextActions.length}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-muted">Self-assessments</span>
                  <span className="fw-semibold">
                    {assessments.filter((a) => a.is_self_assessment).length}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-muted">Assigned by buyers</span>
                  <span className="fw-semibold">{assigned.length}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="text-muted">Buyer organisations</span>
                  <span className="fw-semibold">{buyers.length}</span>
                </div>
              </Card.Body>
            </Card>

            <Card style={dashboardStyles.panel}>
              <div style={dashboardStyles.panelHeader} className="p-3 p-md-4">
                <h5 className="mb-1">Quick links</h5>
                <div className="text-muted small">
                  Common actions for day-to-day work.
                </div>
              </div>
              <Card.Body className="p-3 p-md-4 d-grid gap-2">
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/assessments')}
                >
                  Open All Assessments
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/self-assessments')}
                >
                  Open Self-Assessments
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/my-profile')}
                >
                  Edit Company Profile
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}
